import { logError } from 'services/logging/logging';

/**
 * Checks if a key is an embedded table flat key
 * Embedded table keys use the ".*." pattern to indicate array indexing
 *
 * @param key - The key to check
 * @returns True if the key contains the embedded table array indicator
 *
 * @example
 * isEmbeddedTableFlatKey("packaging_units.*.length") → true
 * isEmbeddedTableFlatKey("product_name") → false
 */
export const isEmbeddedTableFlatKey = (key: string): boolean => {
  return key.includes('.*.');
};

/**
 * Extracts the child key from an embedded table flat key
 * Returns the original key if it's not an embedded table key
 *
 * @param key - The key to extract from (can be embedded table key or regular key)
 * @returns The child key portion for embedded tables, or the original key for regular columns
 *
 * @example
 * extractChildKey("packaging_units.*.length") → "length"
 * extractChildKey("product_name") → "product_name"
 */
export const extractChildKey = (key: string): string => {
  if (!isEmbeddedTableFlatKey(key)) {
    return key;
  }

  const parts = key.split('.*.');

  if (parts.length !== 2 || !parts[1]) {
    logError(`Invalid flat key format: "${key}". Expected exactly one ".*." separator with non-empty parts.`);
    return key;
  }

  return parts[1];
};

/**
 * Extracts the parent key from an embedded table flat key
 * Returns the original key if it's not an embedded table key
 *
 * @param key - The key to extract from (can be embedded table key or regular key)
 * @returns The parent key portion for embedded tables, or the original key for regular columns
 *
 * @example
 * extractParentKey("packaging_units.*.length") → "packaging_units"
 * extractParentKey("product_name") → "product_name"
 */
export const extractParentKey = (key: string): string => {
  if (!isEmbeddedTableFlatKey(key)) {
    return key;
  }
  const childKey = extractChildKey(key);
  return key.replace(`.*.${childKey}`, '');
};
